import { useCallback, useEffect, useRef, useState } from 'react'

import {
  ARIA_OPENING_CUE,
  AriaLiveModality,
  AssistantRealtimeState,
  AssistantSpeakerRole,
  AssistantVoiceStatus,
  type AssistantLiveSessionConfig,
  type AssistantLiveTokenResult
} from '../../../../../../pure-js/types/AppAssistant'
import {
  AssistantMessageKey,
  AssistantMessageSource,
  type AssistantMessage
} from '../../../../../../pure-js/libs/assistantMessages'
import {
  appendAriaTranscriptChunk,
  completeAriaTranscriptTurns,
  completeAriaTranscriptTurnsFor,
  type AriaTranscriptTurn
} from '../../../../../../pure-js/libs/ariaTranscript'
import {
  type AssistantRealtimeCallbacks,
  type AssistantRealtimeTransport,
  type AssistantTranscriptEvent
} from './AssistantRealtimeTransport'
import { createAssistantAudioPipeline } from './assistantAudioPipeline'
import type { ApiResponse } from '../../../../apiLayer/types'
import { USE_MOCKS } from '../../../../apiLayer/processHandlers/config'
import {
  createAssistantToolDispatcher,
  type AssistantDispatcherDeps,
  type AssistantToolDispatcher
} from './assistantToolDispatcher'

/** What the stubbed voice session "says" in mock mode, so the transcript and subtitles have content. */
const MOCK_SPOKEN_REPLY = 'Hi — good to hear you. Want to talk about how your last session went, or about your goals?'

type TransportFactory = (
  config: AssistantLiveSessionConfig,
  callbacks: AssistantRealtimeCallbacks
) => AssistantRealtimeTransport | Promise<AssistantRealtimeTransport>

export type UseAssistantRealtimeSessionParams = AssistantDispatcherDeps & {
  /** Mints an ephemeral token via the authenticated backend callable, for the modality it is handed. */
  mintToken: (modality: AriaLiveModality) => Promise<ApiResponse<AssistantLiveTokenResult>>
  /** Whether this session speaks or writes. A TEXT session touches no microphone and plays nothing, so
   * it never asks for a permission it cannot use. */
  modality?: AriaLiveModality
  /** Renders a keyed backend message in the user's language (the hook itself has no `t`). */
  resolveMessage: (message: AssistantMessage) => string
  /**
   * Nobody has spoken for a while. Defaults to closing the session; the coach overrides it so the
   * transcript is saved on the way out.
   */
  onIdleTimeout?: () => void
  /** Build a transport from the session config (overridable in tests). */
  createTransport?: TransportFactory
  /**
   * What Aria is asked to open the session with. Defaults to a greeting; the coach overrides it so a
   * message she typed but never spoke is read aloud when the user switches to voice.
   */
  resolveOpeningCue?: () => string
  /**
   * Replaces the support assistant's whitelist with a different one. The 1:1 coach has its own single
   * tool and none of the assistant's, so it must not inherit navigation or diagnostics by default.
   */
  dispatchTool?: AssistantToolDispatcher
}

/**
 * How long a session may sit in silence before it closes itself.
 *
 * An abandoned session is the only genuinely expensive way to use this: an open tab streams the
 * user's microphone to Google for the whole token lifetime (12 min for the assistant, 30 for the
 * coach) and bills for every second of it. Long enough that thinking never trips it.
 */
const IDLE_SESSION_TIMEOUT_MS = 120_000 // 2 min

/** Far longer than a spoken session: a typed one holds no microphone, and quiet minutes are ordinary. */
const IDLE_TEXT_SESSION_TIMEOUT_MS = 900_000 // 15 min

/** Checked on a coarse interval — the exact closing second does not matter. */
const IDLE_CHECK_INTERVAL_MS = 15_000 // 15 s

export type UseAssistantRealtimeSessionResult = {
  status: AssistantVoiceStatus
  isLive: boolean
  /** She owes a reply and has not started it — the pause before her first words, made visible. */
  isAriaThinking: boolean
  muted: boolean
  error?: string
  /** The conversation so far, in the order it was spoken. */
  turns: AriaTranscriptTurn[]
  /**
   * Live loudness, 0–1, sampled on demand rather than stored in state. An animation reads these every
   * frame; putting them in state would re-render the whole panel ~20 times a second, which is exactly
   * the kind of main-thread load that starves audio capture.
   */
  getInputLevel: () => number
  getOutputLevel: () => number
  /**
   * Identifies the session the current turns came from, and stays the same after it stops. Turn ids restart
   * from zero on every connect, so anything storing spoken turns must qualify them with this.
   */
  getSessionKey: () => number
  /** The turns right now. `turns` is state, so it lags a `stop()` that saving runs in the same tick as. */
  getTurns: () => AriaTranscriptTurn[]
  start: () => Promise<void>
  stop: () => void
  /** True when it reached the socket. False means no live session, so the caller still owns the text. */
  sendText: (text: string) => boolean
  toggleMute: () => void
}

/** How long the user has to be quiet before their pause counts as Aria thinking rather than them pausing. */
const ARIA_THINKING_AFTER_SILENCE_MS = 500 // 0.5 s

const startFailedMessage: AssistantMessage = {
  source: AssistantMessageSource.TRANSLATION,
  key: AssistantMessageKey.LIVE_VOICE_START_FAILED
}

const microphoneBlockedMessage: AssistantMessage = {
  source: AssistantMessageSource.TRANSLATION,
  key: AssistantMessageKey.LIVE_VOICE_MICROPHONE_BLOCKED
}

/** The browser's name for a microphone it will not hand over: refused in the prompt or blocked in the site settings. */
const MICROPHONE_BLOCKED_ERROR_NAME = 'NotAllowedError'

/** What the person reads when a session could not start: how to unblock the microphone, else the failure itself. */
export const resolveStartFailureText = (
  error: unknown,
  resolveMessage: (message: AssistantMessage) => string
): string => {
  // Read by name: not every browser makes a DOMException an Error.
  const isMicrophoneBlocked = (error as { name?: unknown } | null)?.name === MICROPHONE_BLOCKED_ERROR_NAME
  if (isMicrophoneBlocked) return resolveMessage(microphoneBlockedMessage)
  if (error instanceof Error) return error.message

  return resolveMessage(startFailedMessage)
}

// Imported on demand so the Live API SDK stays out of the shell bundle. Under `VITE_USE_MOCKS` the
// in-memory stub stands in, so the voice UI can be worked on without a Gemini Live session.
const loadTransportModule = () => (USE_MOCKS ? import('./StubRealtimeTransport') : import('./GeminiLiveTransport'))

/**
 * Fetch the transport chunk without needing a token yet, so the download overlaps the mint instead of
 * queueing behind it. The browser caches the module, so the call inside `start` resolves immediately.
 */
export const prefetchRealtimeTransport = (): void => {
  void loadTransportModule().catch(() => undefined)
}

const defaultTransportFactory: TransportFactory = async (config, callbacks) => {
  const module = await loadTransportModule()
  if ('StubRealtimeTransport' in module) {
    return new module.StubRealtimeTransport(callbacks, { cannedReply: MOCK_SPOKEN_REPLY })
  }
  return new module.GeminiLiveTransport(config, callbacks)
}

/** Session keys count on from the page's load time, so a session after a reload never reuses one from before it. */
const SESSION_KEY_BASE = Date.now()

export function useAssistantRealtimeSession(
  params: UseAssistantRealtimeSessionParams
): UseAssistantRealtimeSessionResult {
  const [status, setStatus] = useState<AssistantVoiceStatus>(AssistantVoiceStatus.IDLE)
  const [muted, setMuted] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [turns, setTurns] = useState<AriaTranscriptTurn[]>([])
  // She owes a reply she has not begun. Not per-tick: it flips at most twice a turn.
  const [isAriaThinking, setIsAriaThinking] = useState(false)

  const transportRef = useRef<AssistantRealtimeTransport | undefined>(undefined)
  const audioRef = useRef(createAssistantAudioPipeline())
  // start() awaits three times; every await point compares this against its own id so a stop() or an
  // unmount that lands mid-flight cannot be overwritten by the run it cancelled.
  const startRunIdRef = useRef(0)
  // The run that produced the turns held now: a stop or a lost connection moves the run on, not the turns.
  const turnsRunIdRef = useRef(0)
  // Last time either side made a sound; drives the idle close below.
  const lastActivityAtRef = useRef(0)
  // Fires once the user has actually stopped, so their own pauses mid-sentence do not read as her thinking.
  const thinkingTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const stopWaitingForAria = useCallback(() => {
    clearTimeout(thinkingTimerRef.current)
    setIsAriaThinking(false)
  }, [])
  const waitForAriaAfterSilence = useCallback(() => {
    clearTimeout(thinkingTimerRef.current)
    thinkingTimerRef.current = setTimeout(() => setIsAriaThinking(true), ARIA_THINKING_AFTER_SILENCE_MS)
  }, [])
  const paramsRef = useRef(params)
  paramsRef.current = params

  // Mirrored into a ref because saving a conversation happens in the same tick as the stop that
  // releases the last of it, and state is a render behind.
  const turnsRef = useRef<AriaTranscriptTurn[]>([])
  const applyTurns = useCallback((update: (previous: AriaTranscriptTurn[]) => AriaTranscriptTurn[]) => {
    turnsRef.current = update(turnsRef.current)
    setTurns(turnsRef.current)
  }, [])
  const getTurns = useCallback(() => turnsRef.current, [])

  // Cleared before the close, so the state callback the close fires can tell our own stop from one the
  // server started and does not recurse into this.
  const stop = useCallback(() => {
    startRunIdRef.current += 1
    const transport = transportRef.current
    transportRef.current = undefined
    transport?.close()
    audioRef.current.teardown()
    stopWaitingForAria()
    applyTurns(completeAriaTranscriptTurns)
    setStatus(AssistantVoiceStatus.CLOSED)
  }, [applyTurns, stopWaitingForAria])

  /**
   * Release everything after a close or a failure we did not ask for.
   *
   * Without this the microphone kept capturing and streaming to a dead socket — the recording
   * indicator never went off — and `start()` refused to run because it still saw a transport, so every
   * retry silently did nothing. The idle timer could not save it either: it only runs while live.
   */
  const releaseAfterUnexpectedEnd = useCallback(() => {
    if (!transportRef.current) return
    startRunIdRef.current += 1
    transportRef.current = undefined
    audioRef.current.teardown()
    stopWaitingForAria()
    applyTurns(completeAriaTranscriptTurns)
  }, [applyTurns, stopWaitingForAria])

  /*
   * Transcription is rendered as it arrives, and has to stay that way.
   *
   * Pacing the captions to Aria's voice needs a timer ticking through her whole reply, and every tick
   * is a state update that re-renders the transcript — main-thread work competing with the thread that
   * ships microphone chunks. Audio then reaches Gemini seconds late, so her answer comes seconds late,
   * and the user's own lagging transcription surfaces mid-reply as if a stranger had said it. Revealing
   * the words at speaking pace is `AriaSubtitles`' job, on the DOM node and off the render path.
   */
  const buildCallbacks = useCallback(
    (dispatch: AssistantToolDispatcher, runId: number) => ({
      onAudio: (pcm: Int16Array) => {
        stopWaitingForAria()
        audioRef.current.enqueuePlayback(pcm)
      },
      onUserTranscript: (event: AssistantTranscriptEvent) => {
        lastActivityAtRef.current = Date.now()
        waitForAriaAfterSilence()
        applyTurns((prev) => appendAriaTranscriptChunk(prev, event.role, event.text))
      },
      onAssistantTranscript: (event: AssistantTranscriptEvent) => {
        lastActivityAtRef.current = Date.now()
        stopWaitingForAria()
        applyTurns((prev) => appendAriaTranscriptChunk(prev, event.role, event.text))
      },
      // Barge-in drops the audio that was queued, so those words are never heard — but they were still
      // said, and the transcript is the record of the conversation.
      onInterrupted: () => audioRef.current.stopPlayback(),
      // Aria's turn only. `turnComplete` says the MODEL has finished; input transcription lags the
      // audio, so closing the user's turn here left the tail of a sentence to open a second bubble.
      onTurnComplete: () => applyTurns((prev) => completeAriaTranscriptTurnsFor(prev, AssistantSpeakerRole.ASSISTANT)),
      onToolCall: dispatch,
      /*
       * Both are ignored once this run has been superseded. One callback set is shared by every
       * transport the hook creates, so closing a cancelled run's socket used to run the teardown
       * against whichever run had replaced it — killing the live session and the shared audio pipeline
       * with it.
       */
      onError: (err: Error) => {
        if (runId !== startRunIdRef.current) return
        releaseAfterUnexpectedEnd()
        setError(err.message)
        setStatus(AssistantVoiceStatus.ERROR)
      },
      onStateChange: (next: AssistantRealtimeState) => {
        if (next !== AssistantRealtimeState.CLOSED || runId !== startRunIdRef.current) return
        releaseAfterUnexpectedEnd()
        setStatus(AssistantVoiceStatus.CLOSED)
      }
    }),
    [applyTurns, releaseAfterUnexpectedEnd, stopWaitingForAria, waitForAriaAfterSilence]
  )

  const start = useCallback(async () => {
    if (transportRef.current) return

    const runId = ++startRunIdRef.current
    const isStale = () => runId !== startRunIdRef.current

    setError(undefined)
    turnsRunIdRef.current = SESSION_KEY_BASE + runId
    applyTurns(() => [])

    let stream: MediaStream | undefined
    let transport: AssistantRealtimeTransport | undefined
    // Only this run's own resources: the pipeline is shared, so a cancelled run must never tear down
    // the audio of the run that replaced it.
    const releaseOwnResources = () => {
      // Only ours: a run that has been superseded must not unregister the transport that replaced it.
      if (transport && transportRef.current === transport) transportRef.current = undefined
      transport?.close()
      stream?.getTracks().forEach((track) => track.stop())
    }
    const failWith = (message: string) => {
      releaseOwnResources()
      audioRef.current.teardown()
      stopWaitingForAria()
      setError(message)
      setStatus(AssistantVoiceStatus.ERROR)
    }

    const modality = paramsRef.current.modality ?? AriaLiveModality.AUDIO
    const isTextSession = modality === AriaLiveModality.TEXT

    try {
      setStatus(isTextSession ? AssistantVoiceStatus.CONNECTING : AssistantVoiceStatus.REQUESTING_MIC)

      // The microphone, the token and the transport chunk need nothing from each other, so they are
      // started together: run serially they add up, and the mint is a cold callable often enough that
      // the sum was most of the wait before Aria could say anything. A typed session asks for no
      // microphone at all — prompting for a permission it will never use is its own kind of broken.
      const micRequest = isTextSession ? undefined : navigator.mediaDevices.getUserMedia({ audio: true })
      const mintRequest = paramsRef.current.mintToken(modality)
      prefetchRealtimeTransport()

      // A rejected mint must not surface as an unhandled rejection while the mic prompt is still open.
      const settled = await Promise.allSettled([micRequest, mintRequest])
      if (settled[0].status === 'rejected') throw settled[0].reason
      stream = settled[0].value
      if (isStale()) return releaseOwnResources()

      setStatus(AssistantVoiceStatus.CONNECTING)
      if (settled[1].status === 'rejected') throw settled[1].reason
      const minted = settled[1].value
      if (isStale()) return releaseOwnResources()

      if (!minted.success || !minted.data.config) {
        failWith(paramsRef.current.resolveMessage(minted.data.unavailable ?? startFailedMessage))
        return
      }

      const dispatch = paramsRef.current.dispatchTool ?? createAssistantToolDispatcher(paramsRef.current)
      const createTransport = paramsRef.current.createTransport ?? defaultTransportFactory
      transport = await createTransport(minted.data.config, buildCallbacks(dispatch, runId))
      if (isStale()) return releaseOwnResources()

      await transport.connect()
      if (isStale()) return releaseOwnResources()

      const connected = transport
      lastActivityAtRef.current = Date.now()

      // Registered before the two slow steps below: `releaseAfterUnexpectedEnd` no-ops on an
      // unregistered transport, leaving a hot microphone on a socket that died while they ran.
      transportRef.current = transport

      /*
       * The cue goes out BEFORE the microphone starts streaming, and the order is the whole point.
       *
       * Gemini's `ActivityHandling` defaults to `START_OF_ACTIVITY_INTERRUPTS`: any detected voice
       * activity cuts off the model's current response. With the mic already open, a cough, a chair or
       * the room's own noise floor could cancel Aria's greeting while she was still generating it —
       * silently, since a cancelled turn emits nothing. Nobody is expected to speak before her first
       * word, so there is nothing to lose by opening the mic a moment later.
       */
      // An empty cue means "say nothing yet" — a typed session joining a conversation already in flight
      // has nothing to open, and greeting there would talk over the exchange the user is mid-way through.
      const openingCue = paramsRef.current.resolveOpeningCue?.() ?? ARIA_OPENING_CUE
      if (openingCue) connected.sendText(openingCue)

      if (stream) {
        await audioRef.current.attachMicStream(stream, (pcm) => connected.sendAudioChunk(pcm))
        if (isStale()) return releaseOwnResources()
      }

      setStatus(AssistantVoiceStatus.LIVE)
      // The opening cue went with the connect, so she owes a greeting from this moment — which is the
      // longest she is ever silent, because it is generated from the whole context block.
      waitForAriaAfterSilence()
    } catch (err) {
      if (isStale()) return releaseOwnResources()
      failWith(resolveStartFailureText(err, paramsRef.current.resolveMessage))
    }
  }, [applyTurns, buildCallbacks, stopWaitingForAria, waitForAriaAfterSilence])

  const sendText = useCallback(
    (text: string): boolean => {
      // Answers whether it was actually delivered. A caller that owns the user's own words needs to
      // know: silently dropping a typed message on a socket that is not up loses what they wrote.
      const transport = transportRef.current
      if (!transport) return false

      // A typed session has no input transcription, so this is the only place the user's own words
      // enter the transcript. A spoken one must NOT do this, or every sentence lands twice.
      if (paramsRef.current.modality === AriaLiveModality.TEXT) {
        lastActivityAtRef.current = Date.now()
        waitForAriaAfterSilence()
        applyTurns((prev) =>
          completeAriaTranscriptTurnsFor(
            appendAriaTranscriptChunk(prev, AssistantSpeakerRole.USER, text),
            AssistantSpeakerRole.USER
          )
        )
      }

      transport.sendText(text)
      return true
    },
    [applyTurns, waitForAriaAfterSilence]
  )

  // Stable identities: the animation loop holds these across the whole session.
  const getInputLevel = useCallback(() => audioRef.current.getInputLevel(), [])
  const getOutputLevel = useCallback(() => audioRef.current.getOutputLevel(), [])
  // Read at the moment turns are stored, so it costs no render of its own.
  const getSessionKey = useCallback(() => turnsRunIdRef.current, [])

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev
      audioRef.current.setMuted(next)
      return next
    })
  }, [])

  const isLive = status === AssistantVoiceStatus.LIVE

  // Close a session nobody is using rather than streaming an empty room for the token's whole life.
  useEffect(() => {
    if (!isLive) return
    const timer = setInterval(() => {
      const idleTimeoutMs =
        paramsRef.current.modality === AriaLiveModality.TEXT ? IDLE_TEXT_SESSION_TIMEOUT_MS : IDLE_SESSION_TIMEOUT_MS
      if (Date.now() - lastActivityAtRef.current < idleTimeoutMs) return
      const handleIdle = paramsRef.current.onIdleTimeout
      if (handleIdle) handleIdle()
      else stop()
    }, IDLE_CHECK_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [isLive, stop])

  useEffect(() => () => stop(), [stop])

  return {
    status,
    isAriaThinking,
    isLive,
    muted,
    error,
    turns,
    getInputLevel,
    getOutputLevel,
    getSessionKey,
    getTurns,
    start,
    stop,
    sendText,
    toggleMute
  }
}
